/**
 * Eligibility Verification API Service
 * Medi-Cal FEC: pre-screening, real-time verification, batch processing,
 * credential management, and ECM integration.
 */

import { apiClient } from '../apiClient';

// ── Types ──

export interface PreScreenInput {
  household_size: number;
  monthly_income: number;
  age: number;
  is_pregnant?: boolean;
  is_disabled?: boolean;
  county?: string;
  state_code?: string;
}

export interface PreScreenResult {
  fpl_percentage: number;
  annual_income: number;
  household_size: number;
  fpl_base_amount: number;
  eligible_programs: EligibleProgram[];
  county_mcps: MCPInfo[];
  recommended_actions: string[];
  state_code: string;
  screening_date: string;
}

export interface EligibleProgram {
  program_name: string;
  aid_code_examples: string[];
  fpl_threshold_pct: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  category: string;
  notes: string;
}

export interface MCPInfo {
  plan_name: string;
  plan_type: string;
  mcp_model: string;
  county_code: string;
  county_name: string;
  is_default_assignment: boolean;
  phone_number?: string;
  website?: string;
}

export interface VerifyInput {
  member_id: number;
  reason: 'ECM_REFERRAL' | 'ENROLLMENT_CHECK' | 'RENEWAL' | 'MANUAL';
  force_channel?: 'DIRECT_CAMMIS' | 'CLEARINGHOUSE' | 'PRE_SCREEN';
}

export interface EligibilityResult {
  verification_id?: number;
  is_eligible: boolean | null;
  eligibility_status: string;
  aid_code: string;
  benefits_scope: string;
  effective_date: string | null;
  termination_date: string | null;
  mcp_plan_name: string;
  mcp_plan_id: string;
  mcp_model_type: string;
  mcp_county: string;
  county_code: string;
  county_name: string;
  share_of_cost: number | null;
  evc_number: string;
  other_health_coverage: any[];
  query_channel?: string;
  verification_status: string;
  response_time_ms?: number;
  from_cache?: boolean;
  errors: string[];
  pre_screen?: {
    fpl_percentage: number;
    eligible_programs: any[];
    recommended_actions: string[];
    county_mcps: any[];
  };
}

export interface CredentialInput {
  provider_npi: string;
  medi_cal_provider_number: string;
  cammis_submitter_id?: string;
  cammis_user_id?: string;
  cammis_pin?: string;
  clearinghouse_provider_id?: string;
  clearinghouse_name?: string;
  delegation_authorized_by?: string;
}

export interface CredentialStatus {
  has_credentials: boolean;
  status: string;
  channels_available: string[];
  missing: string[];
  provider_npi?: string;
  last_verified?: string;
}

export interface BatchVerifyInput {
  member_ids: number[];
  reason: string;
}

export interface BatchStatus {
  batch_id: number;
  status: string;
  total_members: number;
  completed_count: number;
  failed_count: number;
  created_at: string;
  completed_at: string | null;
}

export interface ECMVerifyResult {
  eligibility: EligibilityResult;
  mcp_ecm_info: any;
  referral_id: number | null;
  routing_recommendation: {
    can_route: boolean;
    reason?: string;
    action: string;
    mcp_plan_name?: string;
    ecm_contact?: any;
  };
}

export interface AidCodeMapping {
  aid_code: string;
  program_name: string;
  program_category: string;
  benefits_scope: string;
  magi_based: boolean;
  has_share_of_cost: boolean;
  age_group: string;
  description: string;
}

// ── API Methods ──

export const eligibilityApi = {
  // Pre-screening
  preScreen: (data: PreScreenInput) =>
    apiClient.post<PreScreenResult>('/api/eligibility/pre-screen', data),

  getFPLThresholds: (year: number) =>
    apiClient.get(`/api/eligibility/fpl-thresholds/${year}`),

  getMCPDirectory: (stateCode: string, county?: string) =>
    apiClient.get(`/api/eligibility/mcp-directory/${stateCode}`, { params: county ? { county } : {} }),

  getAidCodes: () =>
    apiClient.get<{ aid_codes: AidCodeMapping[] }>('/api/eligibility/aid-codes'),

  // Verification
  verify: (data: VerifyInput) =>
    apiClient.post<EligibilityResult>('/api/eligibility/verify', data),

  verifyBatch: (data: BatchVerifyInput) =>
    apiClient.post<{ batch_id: number; status: string }>('/api/eligibility/verify/batch', data),

  getVerification: (id: number) =>
    apiClient.get<EligibilityResult>(`/api/eligibility/verify/${id}`),

  getMemberHistory: (memberId: number, limit: number = 10) =>
    apiClient.get(`/api/eligibility/member/${memberId}/history`, { params: { limit } }),

  getMemberCurrent: (memberId: number) =>
    apiClient.get<EligibilityResult>(`/api/eligibility/member/${memberId}/current`),

  getBatchStatus: (batchId: number) =>
    apiClient.get<BatchStatus>(`/api/eligibility/batch/${batchId}`),

  // ECM Integration
  ecmVerifyAndRoute: (memberId: number, referralId?: number) =>
    apiClient.post<ECMVerifyResult>('/api/eligibility/ecm/verify-and-route', {
      member_id: memberId,
      referral_id: referralId,
    }),

  getMCPECMInfo: (planName: string) =>
    apiClient.get(`/api/eligibility/ecm/mcp/${encodeURIComponent(planName)}/info`),

  // Credentials (admin)
  storeCredentials: (data: CredentialInput) =>
    apiClient.post('/api/eligibility/credentials', data),

  getCredentialStatus: () =>
    apiClient.get<CredentialStatus>('/api/eligibility/credentials/status'),

  validateCredentials: () =>
    apiClient.post('/api/eligibility/credentials/validate'),

  authorizeDelegation: (authorizedBy: string, baaOnFile: boolean, posAgreementSigned: boolean) =>
    apiClient.post('/api/eligibility/credentials/authorize-delegation', {
      authorized_by: authorizedBy,
      baa_on_file: baaOnFile,
      pos_agreement_signed: posAgreementSigned,
    }),
};

export default eligibilityApi;
